import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { answerPropertyQuestion } from '../../../lib/ai-service';

const AskQuestionModal = ({ listing, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    question: ''
  });
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: leadData, error: leadError } = await supabase?.from('leads')?.insert([
          {
            listing_id: listing?.id,
            type: 'enquiry',
            name: formData?.name,
            email: formData?.email,
            phone: formData?.phone,
            message: formData?.question,
            stage: 'new'
          }
        ])?.select()?.single();

      if (leadError) throw leadError;

      try {
        const aiAnswer = await answerPropertyQuestion(
          {
            address: listing?.properties?.address,
            type: listing?.properties?.type,
            beds: listing?.properties?.beds,
            baths: listing?.properties?.baths,
            price: listing?.price_or_rent,
            description: listing?.description_long
          },
          formData?.question
        );
        setAiResponse(aiAnswer);
      } catch (aiError) {
        console.error('AI response error:', aiError);
        setAiResponse('Thank you for your question. An agent will respond to you shortly via email.');
      }

      setFormData({ name: '', email: '', phone: '', question: '' });
    } catch (error) {
      console.error('Error submitting question:', error);
      setError('Failed to submit question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Ask a Question</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>

        <div className="p-6">
          {aiResponse ? (
            <div className="space-y-4">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <Icon name="Sparkles" size={20} color="var(--color-primary)" />
                  <p className="font-semibold text-foreground">AI Assistant Response</p>
                </div>
                <p className="text-muted-foreground leading-relaxed">{aiResponse}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                An agent will also follow up with you directly via email.
              </p>
              <Button variant="default" fullWidth onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <Input
                label="Your Name"
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                label="Email Address"
                type="email"
                value={formData?.email}
                onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                label="Phone Number (Optional)"
                type="tel"
                value={formData?.phone}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                disabled={loading}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Your Question <span className="text-error">*</span>
                </label>
                <textarea
                  value={formData?.question}
                  onChange={(e) => setFormData({ ...formData, question: e?.target?.value })}
                  required
                  disabled={loading}
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Ask anything about this property..."
                />
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" fullWidth onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" fullWidth loading={loading}>
                  Submit Question
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskQuestionModal;